import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import {
  CalendarDays, Users, Scissors, Trash2, ShieldOff,
  ScrollText, Clock, LogOut, Star, Menu, X, UserCircle,
  ChevronLeft, ChevronRight, Shield, Tv, MessageSquare, KeyRound, Smartphone,
} from "lucide-react";
import { useState, useEffect } from "react";/**
 * AdminLayout
 * - Left: navigation sidebar (Booking management menus)
 * - Center: page content (Calendar / Customers / etc.)
 * - Right: Turn Board sidebar (iframe to /turn-board) — shown ONLY on /admin (Calendar)
 *   so owner/receptionist can monitor turns while managing bookings, like the user mockup.
 * The right panel can be collapsed if not needed.
 */
export default function AdminLayout({ children, title, showTurnBoard = true, fillViewport = false }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("zen_sidebar_collapsed") === "1");
  const [tbCollapsed, setTbCollapsed] = useState(() => localStorage.getItem("zen_turnboard_collapsed") === "1");

  useEffect(() => {
    localStorage.setItem("zen_sidebar_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);
  useEffect(() => {
    localStorage.setItem("zen_turnboard_collapsed", tbCollapsed ? "1" : "0");
  }, [tbCollapsed]);

  const ownerLinks = [
    { to: "/admin", label: "Calendar", icon: CalendarDays },
    { to: "/admin/customers", label: "Customers (CRM)", icon: Star },
    { to: "/admin/reviews", label: "Reviews", icon: MessageSquare },
    { to: "/admin/services", label: "Services", icon: Scissors },
    { to: "/admin/staff", label: "Staff & Skills", icon: Users },
    { to: "/admin/users", label: "User Accounts", icon: UserCircle },
    { to: "/admin/business-hours", label: "Business Hours", icon: Clock },
    { to: "/admin/backup", label: "Backup & Safety", icon: Shield },
    { to: "/admin/blacklist", label: "Blacklist", icon: ShieldOff },
    { to: "/admin/trash", label: "Trash", icon: Trash2 },
    { to: "/admin/activity-log", label: "Activity Log", icon: ScrollText },
    { to: "/turn-board", label: "Turn Board (Full)", icon: Tv, external: true },
  ];
  const recepLinks = [
    { to: "/admin", label: "Calendar", icon: CalendarDays },
    { to: "/admin/customers", label: "Customers (CRM)", icon: Star },
    { to: "/admin/reviews", label: "Reviews", icon: MessageSquare },
    { to: "/admin/blacklist", label: "Blacklist", icon: ShieldOff },
    { to: "/admin/trash", label: "Trash", icon: Trash2 },
    { to: "/turn-board", label: "Turn Board (Full)", icon: Tv, external: true },
  ];
  const links = user?.role === "owner" ? ownerLinks : recepLinks;

  const onLogout = async () => { await logout(); nav("/login"); };

  // Owner self-rename + repassword (used after the salon went public so the
  // initial throwaway hongphuc1510 credentials could be retired).
  const [credOpen, setCredOpen] = useState(false);
  const [credU, setCredU] = useState("");
  const [credP, setCredP] = useState("");
  const [credBusy, setCredBusy] = useState(false);
  const openCred = () => { setCredU(user?.username || ""); setCredP(""); setCredOpen(true); };
  const saveCred = async () => {
    if (!credU.trim() || credP.length < 6) {
      toast.error("Password must be ≥6 characters"); return;
    }
    setCredBusy(true);
    try {
      await api.post("/admin/owner/change-credentials", {
        new_username: credU.trim(), new_password: credP,
      });
      toast.success("Account updated — please sign in again");
      setCredOpen(false);
      await logout(); nav("/login");
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setCredBusy(false); }
  };

  // One-click "create or reset the mobile viewer account". Credentials are
  // INTENTIONALLY hidden in the UI — owner memorises them. Anyone shoulder-
  // surfing the sidebar shouldn't be able to read the password.
  const [mvBusy, setMvBusy] = useState(false);
  const createMobileViewer = async () => {
    if (!window.confirm("Reset the mobile viewer account?\n\nThis re-creates the phone-only booking monitor login.\nUse the credentials you've already memorised.")) return;
    setMvBusy(true);
    try {
      await api.post("/admin/users/upsert-mobile-viewer", {
        username: "zen123", password: "12345678910", full_name: "Mobile Viewer",
      });
      toast.success("Mobile viewer account is ready.");
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setMvBusy(false); }
  };

  const sidebarWidth = collapsed ? "w-20" : "w-64";
  // Booking ~75% / Turn Board ~25% (user requirement). max-w lets it scale up on wide screens.
  const tbWidth = tbCollapsed ? "w-12" : "w-[20%] min-w-[260px] max-w-[420px]";

  return (
    <div
      className={`bg-zen-bg flex ${fillViewport ? "h-screen overflow-hidden" : "min-h-screen"}`}
      data-testid="admin-layout"
      data-fill-viewport={fillViewport ? "1" : "0"}
    >
      {/* LEFT: Nav Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 ${sidebarWidth} bg-white border-r border-zen-border z-30 transform transition-all duration-300 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 flex flex-col`}
        data-testid="admin-sidebar"
      >
        <div className={`p-6 border-b border-zen-border flex items-center ${collapsed ? "justify-center" : "justify-between"}`}>
          {!collapsed ? (
            <div>
              <div className="font-serif text-2xl text-zen-ink leading-none">Zen</div>
              <div className="text-xs tracking-[0.2em] uppercase text-zen-muted mt-1">Nails Lounge</div>
            </div>
          ) : (
            <div className="font-serif text-2xl text-zen-ink leading-none">Z</div>
          )}
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="hidden lg:flex items-center justify-center w-7 h-7 rounded-full border border-zen-border text-zen-muted hover:text-zen-ink hover:border-zen-primary bg-white absolute -right-3 top-8 shadow-sm"
            data-testid="sidebar-toggle-desktop"
            title={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
        <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
          {links.map((l) => {
            const Active = loc.pathname === l.to;
            const Icon = l.icon;
            const className = `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
              Active
                ? "bg-zen-surface text-zen-ink font-medium"
                : "text-zen-muted hover:text-zen-ink hover:bg-zen-surface/60"
            } ${collapsed ? "justify-center" : ""}`;
            if (l.external) {
              return (
                <a key={l.to} href={l.to} target="_blank" rel="noreferrer"
                   className={className} title={collapsed ? l.label : ""}
                   data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && <span>{l.label}</span>}
                </a>
              );
            }
            return (
              <Link key={l.to} to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                title={collapsed ? l.label : ""}
                className={className}
                onClick={() => setMobileOpen(false)}>
                <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                {!collapsed && <span>{l.label}</span>}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-zen-border bg-white">
          {!collapsed && (
            <>
              <div className="text-xs text-zen-muted">Signed in as</div>
              <div className="font-medium text-zen-ink mb-3 truncate">{user?.full_name || user?.username}</div>
            </>
          )}
          {user?.role === "owner" && (
            <>
              <Button variant="outline" className={`w-full rounded-full border-zen-border mb-2 ${collapsed ? "px-0" : ""}`}
                onClick={createMobileViewer} disabled={mvBusy} data-testid="admin-create-mv-btn"
                title={collapsed ? "Reset Helper Login" : ""}>
                <Smartphone className={`w-4 h-4 ${collapsed ? "" : "mr-2"}`} />
                {!collapsed && (mvBusy ? "Resetting…" : "Reset Helper Login")}
              </Button>
              <Button variant="outline" className={`w-full rounded-full border-zen-border mb-2 ${collapsed ? "px-0" : ""}`}
                onClick={openCred} data-testid="admin-change-cred-btn"
                title={collapsed ? "Account" : ""}>
                <KeyRound className={`w-4 h-4 ${collapsed ? "" : "mr-2"}`} />
                {!collapsed && "Change Account"}
              </Button>
            </>
          )}
          <Button variant="outline" className={`w-full rounded-full border-zen-border ${collapsed ? "px-0" : ""}`}
            onClick={onLogout} data-testid="admin-logout-btn"
            title={collapsed ? "Sign Out" : ""}>
            <LogOut className={`w-4 h-4 ${collapsed ? "" : "mr-2"}`} />
            {!collapsed && "Sign Out"}
          </Button>
        </div>
      </aside>

      {/* Owner change-credentials dialog */}
      <Dialog open={credOpen} onOpenChange={setCredOpen}>
        <DialogContent data-testid="change-cred-dialog">
          <DialogHeader><DialogTitle>Change Owner Account</DialogTitle></DialogHeader>
          <div className="text-xs text-zen-muted">After saving you'll be signed out — sign back in with the new credentials.</div>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-zen-ink">New Username</label>
              <Input value={credU} onChange={(e) => setCredU(e.target.value)} className="bg-zen-surface border-0 mt-1" data-testid="cred-new-username" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-zen-ink">New Password (≥6 chars)</label>
              <Input type="password" value={credP} onChange={(e) => setCredP(e.target.value)} className="bg-zen-surface border-0 mt-1" data-testid="cred-new-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCredOpen(false)}>Cancel</Button>
            <Button onClick={saveCred} disabled={credBusy} className="bg-zen-primary text-white" data-testid="cred-save-btn">
              {credBusy ? "Saving…" : "Save & Sign Out"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CENTER: Main content */}
      <div className={`flex-1 min-w-0 flex flex-col ${fillViewport ? "overflow-hidden" : ""}`}>
        <header className="lg:hidden sticky top-0 z-20 bg-white/85 backdrop-blur border-b border-zen-border px-4 py-2 flex items-center justify-between">
          <button className="p-1" onClick={() => setMobileOpen((v) => !v)} data-testid="sidebar-toggle">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <h1 className="font-serif text-xl text-zen-ink">{title}</h1>
          <div className="w-7" />
        </header>
        <main
          className={`flex-1 p-4 lg:p-6 ${fillViewport ? "min-h-0 flex flex-col overflow-hidden" : ""}`}
          data-testid="admin-main"
        >
          {children}
        </main>
      </div>

      {/* RIGHT: Turn Board sidebar (iframe) */}
      {showTurnBoard && (
        <aside
          className={`hidden xl:flex flex-col ${tbWidth} bg-white border-l border-zen-border transition-all duration-300 ${fillViewport ? "flex-shrink-0 h-screen" : "sticky top-0 h-screen"}`}
          data-testid="turnboard-sidebar"
        >
          <div className="flex items-center justify-between p-3 border-b border-zen-border">
            {!tbCollapsed && (
              <div className="flex items-center gap-2">
                <Tv className="w-4 h-4 text-zen-primary" />
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zen-ink">Turn Board</div>
              </div>
            )}
            <button
              onClick={() => setTbCollapsed((c) => !c)}
              className="w-7 h-7 rounded-full border border-zen-border text-zen-muted hover:text-zen-ink hover:border-zen-primary flex items-center justify-center"
              title={tbCollapsed ? "Show Turn Board" : "Hide Turn Board"}
              data-testid="turnboard-toggle"
            >
              {tbCollapsed ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
          {!tbCollapsed && (
            <iframe
              src="/turn-board?embed=1"
              title="Turn Board"
              className="flex-1 w-full border-0"
              data-testid="turnboard-iframe"
            />
          )}
        </aside>
      )}
    </div>
  );
}
